import { Component, inject, Input, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { NotificationDropdownComponent } from '../notification-dropdown/notification-dropdown.component';
import { ProfileDropdownComponent } from '../profile-dropdown/profile-dropdown.component';
import { AuthService } from '../../auth/services/auth.service';
import { TutorialService } from '../../services/tutorial.service';
import { LayoutService } from '../../services/layout.service';
import { TermsDialogComponent } from '../../shared/components/terms-dialog/terms-dialog.component';
import { OrganizationInactiveBannerComponent } from '../../shared/components/organization-inactive-banner/organization-inactive-banner.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    NotificationDropdownComponent,
    ProfileDropdownComponent,
    TermsDialogComponent,
    OrganizationInactiveBannerComponent,
  ],
  template: `
    <div class="layout-container" [class.layout-large]="layoutService.maxWidth() === 'large'">
      <!-- Persistent Header - Only show for authenticated users on non-isolated-portal routes -->
      @if (showIcsChrome()) {
        <header class="app-header">
          <div class="header-content">
            <div class="title-section">
              <h1><a routerLink="/" class="header-title-link">ICS CX Hub</a></h1>
            </div>
            <div class="user-section">
              <button
                pButton
                icon="pi pi-question-circle"
                class="p-button-text p-button-rounded tutorial-btn"
                (click)="startComprehensiveTutorial()"
                title="Help - Start comprehensive tutorial"
                aria-label="Start comprehensive tutorial"
              ></button>
              <app-notification-dropdown></app-notification-dropdown>
              <app-profile-dropdown></app-profile-dropdown>
            </div>
          </div>
        </header>
      }

      <!-- Main Content Area -->
      <main class="main-content" [class]="showIcsChrome() ? 'authenticated' : 'unauthenticated'">
        @if (showIcsChrome()) {
          <app-organization-inactive-banner />
        }
        <router-outlet></router-outlet>
      </main>

      <!-- Persistent Footer - Only show for authenticated users on non-isolated-portal routes -->
      @if (showIcsChrome()) {
        <footer class="app-footer">
          <div class="footer-content">
            <p>© 2026 Bayshore HealthCare All rights reserved.</p>
            <div class="footer-links">
              <a href="https://www.bayshore.ca/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="javascript:void(0)" (click)="showTermsDialog = true">Terms & Conditions</a>
            </div>
          </div>
        </footer>
      }

      <app-terms-dialog [(visible)]="showTermsDialog"></app-terms-dialog>
    </div>
  `,
  styles: [`
    .layout-container {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      background-color: var(--background-color);
      --max-content-width: var(--max-width-standard);
    }

    .layout-container.layout-large {
      --max-content-width: var(--max-width-large);
    }

    .app-header {
      position: sticky;
      top: 0;
      z-index: 100;
      background: var(--background-card);
      border-bottom: 1px solid var(--border-color-light);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px;
      max-width: var(--max-content-width);
      margin: 0 auto;
    }

    .title-section h1 {
      margin: 0;
      font-size: 32px;
      color: var(--text-heading);
      font-weight: 700;
      line-height: 1.4;
      letter-spacing: -0.03125em;
    }

    .header-title-link {
      color: inherit;
      text-decoration: none;

      &:hover {
        opacity: 0.85;
      }
    }

    .user-section {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .tutorial-btn {
      color: var(--text-heading) !important;
      font-size: 1.5rem !important;
      width: 2.5rem;
      height: 2.5rem;
      transition: all 0.2s ease;
    }

    .tutorial-btn:hover {
      color: var(--accent-color) !important;
      background-color: var(--accent-color-light) !important;
    }

    .main-content {
      flex: 1;
      background: var(--background-color);
    }

    .main-content.authenticated {
      /* Styles for authenticated users - with header and footer */
      min-height: 100vh;
    }

    .main-content.unauthenticated {
      /* Styles for non-authenticated users - no header/footer */
      min-height: 100vh;
      background: var(--background-color);
    }

    .app-footer {
      background: var(--background-color);
      border-top: 1px solid var(--border-color-light);
      padding: 1rem 0;
      margin-top: auto;
    }

    .footer-content {
      max-width: var(--max-content-width);
      margin: 0 auto;
      padding: 0 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: var(--text-color-dark);
      font-size: 0.9rem;

      p {
        margin: 0;
      }

      .footer-links {
        a {
          margin-left: 1.5rem;
          color: var(--text-color-dark);
          text-decoration: none;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }

    @media (max-width: 768px) {
      .header-content {
        padding: 16px 24px;
      }

      .footer-content {
        padding: 0 24px;
        flex-direction: column;
        gap: 1rem;
        text-align: center;
      }

      .footer-links a {
        margin: 0 0.75rem;
      }
    }
  `]
})
export class LayoutComponent implements OnInit {
  authService = inject(AuthService);
  tutorialService = inject(TutorialService);
  layoutService = inject(LayoutService);

  // Set by AppComponent for routes hosting an isolated portal (e.g. /ohah-portal)
  // that must never render ICS Referral Hub chrome (header/footer/branding),
  // even when a concurrent ICS session is authenticated in the same tab.
  @Input() hideChrome = false;

  // Computed property to check authentication status
  isAuthenticated = computed(() => this.authService.isAuthenticated());
  showIcsChrome = computed(() => this.isAuthenticated() && !this.hideChrome);
  showTermsDialog = false;

  ngOnInit(): void {
    // Layout initialization logic if needed
  }

  /**
   * Start tutorial for current page/route only
   */
  startRouteTutorial(): void {
    this.tutorialService.startRouteTutorial();
  }

  /**
   * Start comprehensive multi-screen tutorial
   */
  startComprehensiveTutorial(): void {
    this.tutorialService.startComprehensiveTutorial();
  }
}
